import React from 'react';
import { isEmpty } from 'lodash';
import { messageFormat } from '@clubmed/utils';
import { getBaiduMapURL } from 'Components/utils/baidu/baidu.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { getGoogleMapURL } from 'Components/utils/google/google.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { AddressProps } from 'Components/frameworks/address/Address';
import { Address } from 'Components/frameworks/address/Address';
import { BaiduMap } from 'Components/frameworks/map/BaiduMap';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { GoogleMap } from 'Components/frameworks/map/GoogleMap';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore';
import { GoogleStreetView } from 'Components/frameworks/map/GoogleStreetView';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
type Props = {
  access?: Array<{
    iconName?: IconName;
    texts?: Array<string>;
  }>;
  accessTitle?: string;
  address?: AddressProps;
  className?: string;
  isBaidu?: boolean;
  map?: any;
  productId?: string;
  productName?: string;
  streetView?: {
    fov?: number;
    heading?: number;
    latitude: number;
    longitude: number;
    pitch?: number;
  };
};
export const AccessAndMap = ({
  access = [],
  accessTitle,
  address,
  className,
  isBaidu,
  map,
  productId,
  productName,
  streetView,
}: Props) => {
  const { isTablet } = useResponsive();
  const track = useTracking();

  if (!map) {
    return null;
  }

  const ratio = isTablet
    ? {
        w: 780,
        h: 420,
      }
    : {
        w: 290,
        h: 320,
      };
  const mapProps = { ...map, closeLabel: t('common.close'), width: ratio.w, height: ratio.h };
  const coords = {
    latitude: mapProps.latitude,
    longitude: mapProps.longitude,
  };
  const streetViewRatio = {
    w: 170,
    h: 90,
  };
  const mapUrl = isBaidu ? getBaiduMapURL(coords) : getGoogleMapURL(coords);
  const hasStreetView = Boolean(streetView && streetView.latitude && streetView.longitude);
  return (
    <div className={classnames('flex flex-col gap-20 sm:flex-row', className)}>
      <div className="relative isolate sm:w-2/3">
        {isBaidu ? (
          <BaiduMap
            {...mapProps}
            className="rounded-12"
            title={messageFormat(t('common.maps.productBaiduMap')).format({
              productName,
            })}
          />
        ) : (
          <div className="relative">
            <GoogleMap
              {...mapProps}
              className="rounded-12"
              title={messageFormat(t('common.maps.productGoogleMap')).format({
                productName,
              })}
            />
            {hasStreetView && streetView && (
              <div
                style={{
                  height: streetViewRatio.h,
                  width: streetViewRatio.w,
                }}
                className="absolute bottom-0 right-0 m-20"
              >
                <GoogleStreetView
                  {...streetView}
                  className="z-1 rounded-12 border-5 border-solid border-white shadow"
                  closeLabel={t('common.close')}
                  height={streetViewRatio.h}
                  title={messageFormat(t('common.maps.productGoogleStreetView')).format({
                    productName,
                  })}
                  width={streetViewRatio.w}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="sm:w-1/3">
        {address && (
          <div className="mb-20 flex items-center rounded-12 border-1 border-lightGrey bg-white px-20 py-10 last:mb-0">
            <Address
              {...address}
              className="flex-1"
              displayAddOnFirst={Boolean(productId && productId.startsWith('QCHC'))}
            />
            {mapUrl && (
              <DynamicLink
                className="-me-15 p-15 leading-0 text-black"
                data-testid="address-link"
                onClick={() =>
                  track({
                    actionGA: 'mapClick',
                    categoryGA: 'resort',
                    labelGA: 'GoogleMapPage',
                  })
                }
                url={mapUrl}
                target="_blank"
              >
                <Icon hasRtl name={ICONS.ARROW_RIGHT} width="0.625rem" />
              </DynamicLink>
            )}
          </div>
        )}
        {!isEmpty(access) && accessTitle && <h3 className="mb-15 text-14">{accessTitle}</h3>}
        <SeeMore
          classNameButton="text-end"
          isCollapseEnabled={access.length > 3}
          seeLessLabel={t('common.loadLess')}
          seeMoreLabel={t('common.viewMore')}
          seeMoreSize={250}
        >
          {access.map(({ iconName, texts = [] }, i) => (
            <div
              className="mb-15 flex items-center justify-between gap-20 rounded-12 border-1 border-lightGrey bg-white px-20 py-10 last:mb-0"
              data-testid="AccessAndMap-access"
              key={i}
            >
              <div>
                {texts.map((accessTextLine, j) => (
                  <p
                    className={classnames('leading-normal text-black', {
                      'text-12': j,
                      'text-14': !j,
                    })}
                    key={j}
                  >
                    {j ? accessTextLine : <strong className="font-bold">{accessTextLine}</strong>}
                  </p>
                ))}
              </div>
              <Icon className="shrink-0 text-black" name={iconName} width="1rem" />
            </div>
          ))}
        </SeeMore>
      </div>
    </div>
  );
};
